import { upscaleSingle } from "../../../services/api/tools/upscale";
import { urlToFile } from "../../../utils/urlToFile";
import {
  DOWNLOAD_QUALITY_MAX_SIDE,
  getDownloadQualityTierById,
} from "../../../config/tools/downloadQualityTiers";
import { getDownloadFormat } from "../../../config/tools/toolPrepDownloadFormats";
import {
  SOFT_WATERMARK_FONT_MIN,
  SOFT_WATERMARK_FONT_RATIO,
  SOFT_WATERMARK_FONT_WEIGHT,
  SOFT_WATERMARK_PAD,
  SOFT_WATERMARK_TEXT,
} from "../../../config/tools/softWatermarkBrand";

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    if (
      typeof src === "string" &&
      /^https?:\/\//i.test(src) &&
      !src.startsWith("blob:") &&
      !src.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image for export."));
    img.src = src;
  });
}

/** Remote storage URLs taint a canvas. Copy them to a blob URL before export. */
async function materializeExportUrl(url) {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) {
    return { url, revoke: null };
  }
  const file = await urlToFile(url, "download-source.png");
  const blobUrl = URL.createObjectURL(file);
  return { url: blobUrl, revoke: () => URL.revokeObjectURL(blobUrl) };
}

export async function probeImageLongestSide(imageUrl) {
  if (!imageUrl) return 0;
  const img = await loadImageElement(imageUrl);
  return Math.max(img.naturalWidth || 0, img.naturalHeight || 0);
}

export async function imageHasTransparency(imageUrl) {
  if (!imageUrl) return false;
  const img = await loadImageElement(imageUrl);
  const w = Math.min(img.naturalWidth || 0, 64);
  const h = Math.min(img.naturalHeight || 0, 64);
  if (w < 1 || h < 1) return false;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(img, 0, 0, w, h);
  try {
    const { data } = ctx.getImageData(0, 0, w, h);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) return true;
    }
  } catch {
    return false;
  }
  return false;
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error("Could not encode image."));
        else resolve(blob);
      },
      mime,
      quality
    );
  });
}

/**
 * How far from the bottom-right corner until opaque pixels (rounded / cut corners).
 */
function measureBottomRightOpaqueInset(ctx, width, height) {
  const maxProbe = Math.min(Math.floor(Math.min(width, height) * 0.35), 256);
  if (maxProbe < 2) return 0;
  try {
    const size = maxProbe;
    const { data } = ctx.getImageData(width - size, height - size, size, size);
    for (let d = 0; d < size; d += 1) {
      const x = size - 1 - d;
      const y = size - 1 - d;
      if (data[(y * size + x) * 4 + 3] > 24) return d;
    }
    return size;
  } catch {
    return 0;
  }
}

/**
 * Soft watermark — larger, heavier type; still secondary to the subject.
 * Insets further when the export has rounded/transparent corners.
 */
function drawSoftWatermark(ctx, width, height) {
  const fontSize = Math.max(
    SOFT_WATERMARK_FONT_MIN,
    Math.round(Math.min(width, height) * SOFT_WATERMARK_FONT_RATIO)
  );
  const padX = Math.max(8, Math.round(fontSize * 0.5));
  const padY = Math.max(5, Math.round(fontSize * 0.32));
  const radius = Math.max(5, Math.round(fontSize * 0.42));
  const basePad = Math.max(12, Math.round(SOFT_WATERMARK_PAD * (fontSize / 16)));
  const clearInset = measureBottomRightOpaqueInset(ctx, width, height);
  const edgePad = Math.max(basePad, clearInset + Math.round(fontSize * 0.45));

  ctx.save();
  ctx.font = `${SOFT_WATERMARK_FONT_WEIGHT} ${fontSize}px system-ui, Segoe UI, sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  const metrics = ctx.measureText(SOFT_WATERMARK_TEXT);
  const textW = metrics.width;
  const boxW = textW + padX * 2;
  const boxH = fontSize + padY * 2;
  const boxX = width - edgePad - boxW;
  const boxY = height - edgePad - boxH;
  const textX = boxX + padX;
  const textY = boxY + boxH / 2;

  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(boxX, boxY, boxW, boxH, radius);
  } else {
    const r = radius;
    ctx.moveTo(boxX + r, boxY);
    ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + boxH, r);
    ctx.arcTo(boxX + boxW, boxY + boxH, boxX, boxY + boxH, r);
    ctx.arcTo(boxX, boxY + boxH, boxX, boxY, r);
    ctx.arcTo(boxX, boxY, boxX + boxW, boxY, r);
    ctx.closePath();
  }
  ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
  ctx.fill();

  ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
  ctx.lineWidth = Math.max(1.5, fontSize * 0.12);
  ctx.lineJoin = "round";
  ctx.strokeText(SOFT_WATERMARK_TEXT, textX, textY);
  ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
  ctx.fillText(SOFT_WATERMARK_TEXT, textX, textY);

  ctx.restore();
}

/**
 * Resize (fit longest side), optional soft watermark, encode to format.
 */
export async function renderDownloadImageBlob({
  imageUrl,
  maxSide = null,
  softWatermark = false,
  formatId = "png",
  flattenForJpeg = true,
}) {
  const fmt = getDownloadFormat(formatId);
  const img = await loadImageElement(imageUrl);
  let width = img.naturalWidth || 1;
  let height = img.naturalHeight || 1;
  const longest = Math.max(width, height);

  if (maxSide && longest > maxSide) {
    const scale = maxSide / longest;
    width = Math.max(1, Math.round(width * scale));
    height = Math.max(1, Math.round(height * scale));
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable.");

  if (!fmt.supportsTransparency && flattenForJpeg) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
  }

  ctx.drawImage(img, 0, 0, width, height);

  if (softWatermark) {
    drawSoftWatermark(ctx, width, height);
  }

  const quality = fmt.id === "png" ? undefined : 0.92;
  return canvasToBlob(canvas, fmt.mime, quality);
}

function scaleForTarget(sourceSide, targetSide) {
  if (sourceSide <= 0 || targetSide <= sourceSide) return null;
  const ratio = targetSide / sourceSide;
  if (ratio <= 2.2) return "2x";
  return "4x";
}

/**
 * Full editing/AI download pipeline after options are confirmed.
 * @returns {Promise<{ blob: Blob, previewUrl: string, upscaled: boolean, creditsUsed: number }>}
 */
export async function runToolPrepDownloadPipeline({
  imageUrl,
  formatId = "png",
  qualityId = null,
  showQuality = false,
  getToken = null,
  onPhase = null,
}) {
  if (!imageUrl) throw new Error("Nothing to download yet.");

  let workingUrl = imageUrl;
  let upscaled = false;
  let creditsUsed = 0;
  let revokeWorking = null;

  const notify = (phase, detail) => {
    if (typeof onPhase === "function") onPhase(phase, detail);
  };

  try {
    const sourceSide = await probeImageLongestSide(workingUrl);
    const tierMap = getDownloadQualityTierById();
    const tier = showQuality && qualityId ? tierMap[qualityId] : null;

    const needsUpscale =
      Boolean(tier) &&
      sourceSide > 0 &&
      sourceSide < tier.maxSide &&
      qualityId !== "1k";

    if (needsUpscale) {
      notify("upscaling", { target: qualityId });
      if (typeof getToken !== "function") {
        throw new Error("Sign in required to unlock higher quality.");
      }
      const token = await getToken();
      if (!token) throw new Error("Sign in required to unlock higher quality.");

      const scale = scaleForTarget(sourceSide, tier.maxSide) || "2x";
      const file = await urlToFile(workingUrl, "download-source.png");
      const data = await upscaleSingle(token, file, {
        quality: "balanced",
        scale,
        output_format: "png",
      });
      if (!data?.success || !data?.output_url) {
        throw new Error(data?.message || "Upscale failed. Credits were not kept if the job failed.");
      }
      workingUrl = data.output_url;
      upscaled = true;
      creditsUsed = Number(data.credits_used) || tier.credits || 0;
    }

    notify("encoding", { formatId, qualityId });

    const exportSource = await materializeExportUrl(workingUrl);
    workingUrl = exportSource.url;
    revokeWorking = exportSource.revoke;

    const softWatermark = Boolean(
      showQuality && qualityId === "1k" && tier?.softWatermark
    );
    const maxSide =
      showQuality && qualityId === "1k"
        ? DOWNLOAD_QUALITY_MAX_SIDE["1k"]
        : showQuality && qualityId === "2k" && sourceSide < DOWNLOAD_QUALITY_MAX_SIDE["2k"] && !upscaled
          ? DOWNLOAD_QUALITY_MAX_SIDE["2k"]
          : null;

    // After upscale, optionally fit to tier max side
    const fitSide =
      showQuality && tier && upscaled
        ? tier.maxSide
        : softWatermark
          ? DOWNLOAD_QUALITY_MAX_SIDE["1k"]
          : maxSide;

    const blob = await renderDownloadImageBlob({
      imageUrl: workingUrl,
      maxSide: softWatermark ? DOWNLOAD_QUALITY_MAX_SIDE["1k"] : fitSide,
      softWatermark,
      formatId,
    });

    const previewUrl = URL.createObjectURL(blob);
    return { blob, previewUrl, upscaled, creditsUsed };
  } finally {
    if (typeof revokeWorking === "function") revokeWorking();
  }
}
